import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PLANS, getPlan, usesLifetimeCounter, isUnlimited, type PlanId } from "@/config/plans";

export type UsageType = "documents" | "ai_searches" | "pdf_actions" | "share_links" | "sources";

export interface UsageData {
  documents: number;
  lifetimeUploads: number;
  currentStorageCount: number;
  ai_searches: number;
  pdf_actions: number;
  share_links: number;
  sources: number;
}

// Legacy interface for backwards compatibility
export interface LegacyLimits {
  documents: number;
  ai_searches: number;
  pdf_actions: number;
  share_links: number;
  storage_mb: number;
  scanner_pages_per_doc: number;
  merge_docs_max: number;
  ai_search_period: "daily" | "monthly";
  can_export: boolean;
  can_sign: boolean;
  can_team: boolean;
}

export interface UsageLimitsHook {
  usage: UsageData;
  limits: LegacyLimits;
  planId: PlanId;
  planSlug: string; // Alias for planId (backwards compatibility)
  isLoading: boolean;
  checkStorageLimit: () => { allowed: boolean; reason?: string };
  checkLimit: (type: UsageType) => boolean;
  incrementUsage: (type: UsageType) => Promise<boolean>;
  getUsagePercentage: (type: UsageType) => number;
  getRemainingUsage: (type: UsageType) => number;
  canUseFeature: (feature: string) => boolean;
  refreshUsage: () => Promise<void>;
}

// Convert new plan format to legacy limits format
function planToLegacyLimits(planId: PlanId): LegacyLimits {
  const plan = getPlan(planId);
  return {
    documents: plan.limits.storage === Infinity ? -1 : plan.limits.storage,
    ai_searches: plan.limits.aiDaily === Infinity ? -1 : plan.limits.aiDaily,
    pdf_actions: plan.limits.pdfMonthly === Infinity ? -1 : plan.limits.pdfMonthly,
    share_links: plan.limits.shareLinks === Infinity ? -1 : plan.limits.shareLinks,
    storage_mb: -1, // Not used in new system
    scanner_pages_per_doc: -1, // No limit on scanner pages per doc
    merge_docs_max: -1, // No limit on merge docs
    ai_search_period: "daily",
    can_export: plan.features.includes("export_zip"),
    can_sign: true, // All plans can sign
    can_team: plan.features.includes("roles_management"),
  };
}

export function useUsageLimits(): UsageLimitsHook {
  const [usage, setUsage] = useState<UsageData>({
    documents: 0,
    lifetimeUploads: 0,
    currentStorageCount: 0,
    ai_searches: 0,
    pdf_actions: 0,
    share_links: 0,
    sources: 0,
  });
  const [planId, setPlanId] = useState<PlanId>("discovery");
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const plan = getPlan(planId);
  const limits = planToLegacyLimits(planId);

  const fetchUsage = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Get user's subscription plan
      const { data: subscription } = await supabase
        .from("subscriptions")
        .select("plan_id, status, subscription_plans(slug)")
        .eq("user_id", user.id)
        .eq("status", "active")
        .maybeSingle();

      if (subscription?.subscription_plans) {
        const slug = (subscription.subscription_plans as { slug: string }).slug;
        // Map slug to PlanId - includes new plans
        const validPlanIds: PlanId[] = ["discovery", "solo", "famille", "pro", "team", "business", "enterprise"];
        if (validPlanIds.includes(slug as PlanId)) {
          setPlanId(slug as PlanId);
        } else {
          // Try legacy mapping
          const legacyMap: Record<string, PlanId> = {
            'trial': 'discovery',
            'classique': 'solo',
            'personal': 'solo',
            'family': 'famille',
            'premium': 'pro',
            'entreprise': 'business',
          };
          setPlanId(legacyMap[slug] || "discovery");
        }
      } else {
        // Check if user is in trial / discovery
        const { data: trialSub } = await supabase
          .from("subscriptions")
          .select("status, trial_ends_at")
          .eq("user_id", user.id)
          .eq("status", "trialing")
          .maybeSingle();

        if (trialSub) {
          setPlanId("discovery");
        }
      }

      // Get profile with storage counters
      const { data: profile } = await supabase
        .from("profiles")
        .select("lifetime_uploads, current_storage_count")
        .eq("id", user.id)
        .single();

      const lifetimeUploads = profile?.lifetime_uploads || 0;
      const currentStorageCount = profile?.current_storage_count || 0;

      // Get AI searches usage (daily)
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const { data: aiUsage } = await supabase
        .from("usage_tracking")
        .select("count")
        .eq("user_id", user.id)
        .eq("usage_type", "ai_searches")
        .gte("period_start", todayStart.toISOString())
        .maybeSingle();

      // Get PDF actions usage (monthly)
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      const { data: pdfUsage } = await supabase
        .from("usage_tracking")
        .select("count")
        .eq("user_id", user.id)
        .eq("usage_type", "pdf_actions")
        .gte("period_start", monthStart.toISOString())
        .maybeSingle();

      // Get share links count
      const { count: shareCount } = await supabase
        .from("share_links")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      // Get connected sources count (using safe view without tokens)
      const { count: sourcesCount } = await supabase
        .from("connected_accounts_safe")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("status", "connected");

      setUsage({
        documents: currentStorageCount,
        lifetimeUploads,
        currentStorageCount,
        ai_searches: aiUsage?.count || 0,
        pdf_actions: pdfUsage?.count || 0,
        share_links: shareCount || 0,
        sources: sourcesCount || 0,
      });
    } catch (error) {
      console.error("Error fetching usage:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  /**
   * Vérifie si l'utilisateur peut uploader un nouveau document
   * Logique différente pour discovery (lifetime) vs payants (current storage)
   */
  const checkStorageLimit = useCallback((): { allowed: boolean; reason?: string } => {
    if (usesLifetimeCounter(planId)) {
      // Plan Discovery: compteur à vie
      if (usage.lifetimeUploads >= plan.limits.storage) {
        return {
          allowed: false,
          reason: `Vous avez atteint la limite de ${plan.limits.storage} documents du plan gratuit. Passez à un plan supérieur pour continuer.`,
        };
      }
    } else {
      // Plans payants: stockage actif
      if (!isUnlimited(plan.limits.storage) && usage.currentStorageCount >= plan.limits.storage) {
        return {
          allowed: false,
          reason: `Vous avez atteint la limite de ${plan.limits.storage} documents. Supprimez des documents ou passez à un plan supérieur.`,
        };
      }
    }
    return { allowed: true };
  }, [planId, plan, usage]);

  const checkLimit = useCallback(
    (type: UsageType): boolean => {
      if (type === "documents") {
        return checkStorageLimit().allowed;
      }

      const limit = limits[type as keyof typeof limits];
      if (typeof limit !== "number") return true;
      if (limit === -1) return true;
      
      return usage[type] < limit;
    },
    [usage, limits, checkStorageLimit]
  );

  const incrementUsage = useCallback(
    async (type: UsageType): Promise<boolean> => {
      if (!checkLimit(type)) {
        const limitNames: Record<UsageType, string> = {
          documents: "documents",
          ai_searches: "recherches IA",
          pdf_actions: "actions PDF",
          share_links: "liens de partage",
          sources: "sources connectées",
        };
        toast({
          title: "Limite atteinte",
          description: `Vous avez atteint votre limite de ${limitNames[type]}. Passez à un plan supérieur pour continuer.`,
          variant: "destructive",
        });
        return false;
      }

      // Documents sont gérés par triggers, pas besoin d'incrémenter manuellement
      if (type === "documents") return true;

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        const periodType = type === "ai_searches" ? "daily" : "monthly";

        const { data, error } = await supabase.rpc("increment_usage", {
          p_user_id: user.id,
          p_usage_type: type,
          p_period_type: periodType,
        });

        if (error) throw error;

        setUsage((prev) => ({
          ...prev,
          [type]: data || prev[type] + 1,
        }));

        return true;
      } catch (error) {
        console.error("Error incrementing usage:", error);
        return false;
      }
    },
    [checkLimit, toast]
  );

  const getUsagePercentage = useCallback(
    (type: UsageType): number => {
      if (type === "documents") {
        const limit = plan.limits.storage;
        if (isUnlimited(limit)) return 0;
        const count = usesLifetimeCounter(planId) ? usage.lifetimeUploads : usage.currentStorageCount;
        return Math.min((count / limit) * 100, 100);
      }

      const limit = limits[type as keyof typeof limits];
      if (typeof limit !== "number" || limit === -1) return 0;
      return Math.min((usage[type] / limit) * 100, 100);
    },
    [usage, limits, plan, planId]
  );

  const getRemainingUsage = useCallback(
    (type: UsageType): number => {
      if (type === "documents") {
        const limit = plan.limits.storage;
        if (isUnlimited(limit)) return -1;
        const count = usesLifetimeCounter(planId) ? usage.lifetimeUploads : usage.currentStorageCount;
        return Math.max(limit - count, 0);
      }

      const limit = limits[type as keyof typeof limits];
      if (typeof limit !== "number" || limit === -1) return -1;
      return Math.max(limit - usage[type], 0);
    },
    [usage, limits, plan, planId]
  );

  const canUseFeature = useCallback(
    (feature: string): boolean => {
      // Map legacy feature names to new feature names
      const featureMap: Record<string, string> = {
        export: "export_zip",
        sign: "white_label", // All paid plans can sign
        team: "roles_management",
      };
      const mappedFeature = featureMap[feature] || feature;
      return plan.features.includes(mappedFeature) || feature === "sign"; // Sign is always allowed
    },
    [plan]
  );

  return {
    usage,
    limits,
    planId,
    planSlug: planId, // Backwards compatibility alias
    isLoading,
    checkStorageLimit,
    checkLimit,
    incrementUsage,
    getUsagePercentage,
    getRemainingUsage,
    canUseFeature,
    refreshUsage: fetchUsage,
  };
}

// Export for backwards compatibility
export const PLAN_LIMITS = PLANS;
